import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { notificationService, AppNotification } from '../services/notificationService';
import { useAuth } from '../hooks/useAuth';
import { socketService } from '../services/socketService';

const NotificationDropdown: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<AppNotification | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    if (user) {
      // Connect to WebSocket
      try {
        socketService.connect();
      } catch (error) {
        console.error('Error connecting to WebSocket:', error);
      }

      // Fetch initial unread count
      fetchUnreadCount();

      // Poll for updates if WebSocket not connected (fallback)
      const pollInterval = setInterval(() => {
        if (!socketService.isConnected()) {
          fetchUnreadCount();
        }
      }, 5000); // Poll every 5 seconds if WebSocket not available

      // Listen for real-time notifications
      const unsubscribeNotification = socketService.on('new_notification', (notification: AppNotification) => {
        console.log('New notification received:', notification);
        setNotifications(prev => {
          // Check if notification already exists
          const exists = prev.some(n => n.id === notification.id);
          if (exists) return prev;
          return [notification, ...prev];
        });
        setUnreadCount(prev => prev + 1);

        // Show browser notification if permission granted
        if ('Notification' in window && window.Notification.permission === 'granted') {
          new window.Notification(notification.title, {
            body: notification.content || '',
            icon: '/favicon.ico',
          });
        }
      });

      // Listen for unread count updates
      const unsubscribeCount = socketService.on('unread_count', (count: number) => {
        setUnreadCount(count);
      });

      // Request notification permission
      if ('Notification' in window && window.Notification.permission === 'default') {
        window.Notification.requestPermission();
      }

      return () => {
        unsubscribeNotification();
        unsubscribeCount();
        clearInterval(pollInterval);
      };
    } else {
      socketService.disconnect();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService.getNotifications({ page: 1, per_page: 10, unread_only: false });
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh notifications when dropdown opens to get latest
  useEffect(() => {
    if (isOpen && user) {
      fetchNotifications();
    }
  }, [isOpen, user]);

  const fetchUnreadCount = async () => {
    try {
      const data = await notificationService.getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      // Unread count will be updated via WebSocket
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'report':
        return '⚠️';
      case 'comment':
        return '💬';
      case 'comment_reply':
        return '↩️';
      case 'comment_like':
        return '❤️';
      case 'mention':
        return '👤';
      case 'post_update':
        return '📝';
      case 'post_like':
        return '👍';
      case 'post_approved':
        return '✅';
      case 'post_rejected':
        return '❌';
      case 'post_pinned':
        return '📌';
      case 'message':
        return '💌';
      default:
        return '🔔';
    }
  };

  // Real-time current time for accurate time display
  const [currentTime, setCurrentTime] = useState(() => {
    const now = new Date();
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
    const vietnamTime = utcTime + (7 * 60 * 60 * 1000);
    return vietnamTime;
  });

  // Update current time every 1 minute for real-time display
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
      const vietnamTime = utcTime + (7 * 60 * 60 * 1000);
      setCurrentTime(vietnamTime);
    };

    // Update immediately
    updateTime();

    // Then update every 1 minute (60000ms) for accurate time display
    const timeInterval = setInterval(updateTime, 60000);

    return () => clearInterval(timeInterval);
  }, []);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    // Convert to Vietnam timezone (UTC+7)
    const utcTime = date.getTime();
    const vietnamTime = utcTime + (7 * 60 * 60 * 1000);

    // Calculate difference using current time
    const diffMs = currentTime - vietnamTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Vừa xong';
    if (diffMins < 60) return `${diffMins} phút trước`;
    if (diffHours < 24) return `${diffHours} giờ trước`;
    if (diffDays < 7) return `${diffDays} ngày trước`;
    return date.toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button with animated gradient border */}
      {/* Bell button with Sonic Border style */}
      <div className="group relative overflow-hidden p-[1px] rounded-full bg-gradient-sonic-mirrored animate-gradient shadow-sonic z-20">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative flex items-center justify-center w-10 h-10 bg-black/85 hover:bg-black/75 rounded-full text-white/90 hover:text-white transition backdrop-blur-sm"
          aria-label="Notifications"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold px-1 border-2 border-black">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        <div className="pointer-events-none absolute inset-0 z-20 -skew-x-12 overflow-hidden rounded-full">
          <div className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer-sweep" />
        </div>
      </div>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Thông báo</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Đánh dấu tất cả đã đọc
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Đang tải...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">Không có thông báo nào</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition cursor-pointer ${!notification.is_read ? 'bg-blue-50' : ''
                      }`}
                    onClick={() => {
                      if (!notification.is_read) {
                        handleMarkAsRead(notification.id);
                      }

                      // If notification is for a message, navigate to chat
                      if (notification.type === 'message' && notification.related_user_id) {
                        setIsOpen(false);
                        // Get username from related_user if available, otherwise fetch it
                        if (notification.related_user && notification.related_user.username) {
                          window.location.href = `/chat/${notification.related_user.username}`;
                        } else {
                          // Fallback: navigate to chat page and let it handle user lookup
                          window.location.href = `/chat`;
                        }
                        return;
                      }

                      // If notification has related comment, navigate directly to it
                      if (notification.related_comment_id && notification.related_post_id) {
                        setIsOpen(false);
                        const hash = `post-${notification.related_post_id}&comment-${notification.related_comment_id}`;
                        if (window.location.pathname === '/community') {
                          window.location.hash = hash;
                          // Trigger scroll by dispatching hashchange event
                          window.dispatchEvent(new HashChangeEvent('hashchange'));
                        } else {
                          window.location.href = `/community#${hash}`;
                        }
                        return;
                      }

                      // Otherwise, show detail modal
                      setSelectedNotification(notification);
                      setShowDetailModal(true);
                      setIsOpen(false);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-2xl flex-shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></span>
                          )}
                        </div>
                        {notification.content && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                            {notification.content}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            {formatTime(notification.created_at)}
                          </span>
                          {notification.related_post && (
                            <Link
                              to={`/community#post-${notification.related_post_id}`}
                              className="text-xs text-blue-600 hover:text-blue-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Xem bài viết →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 text-center">
              <Link
                to="/notifications"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                onClick={() => setIsOpen(false)}
              >
                Xem tất cả thông báo
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Notification Detail Modal */}
      {showDetailModal && selectedNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <div className="text-3xl flex-shrink-0">
                  {getNotificationIcon(selectedNotification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {selectedNotification.title}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>{formatTime(selectedNotification.created_at)}</span>
                    {!selectedNotification.is_read && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        Chưa đọc
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition p-1"
                aria-label="Đóng"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {selectedNotification.content ? (
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedNotification.content}
                  </p>
                </div>
              ) : (
                <p className="text-gray-500 italic">Không có nội dung chi tiết</p>
              )}

              {/* Related Information */}
              {selectedNotification.type === 'message' && selectedNotification.related_user && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Tin nhắn từ:
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedNotification.related_user.full_name || selectedNotification.related_user.username}
                      </p>
                    </div>
                    <Link
                      to={`/chat/${selectedNotification.related_user.username}`}
                      onClick={() => {
                        setShowDetailModal(false);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition whitespace-nowrap"
                    >
                      Mở cuộc trò chuyện →
                    </Link>
                  </div>
                </div>
              )}

              {selectedNotification.related_post && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        {selectedNotification.related_comment_id ? 'Bình luận liên quan:' : 'Bài viết liên quan:'}
                      </p>
                      <p className="text-sm text-gray-600">{selectedNotification.related_post.title}</p>
                    </div>
                    <Link
                      to={selectedNotification.related_comment_id
                        ? `/community#post-${selectedNotification.related_post_id}&comment-${selectedNotification.related_comment_id}`
                        : `/community#post-${selectedNotification.related_post_id}`
                      }
                      onClick={() => {
                        setShowDetailModal(false);
                        // If already on community page, trigger hash change
                        if (window.location.pathname === '/community') {
                          const hash = selectedNotification.related_comment_id
                            ? `post-${selectedNotification.related_post_id}&comment-${selectedNotification.related_comment_id}`
                            : `post-${selectedNotification.related_post_id}`;
                          window.location.hash = hash;
                          // Force hash change event
                          window.dispatchEvent(new HashChangeEvent('hashchange'));
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition whitespace-nowrap"
                    >
                      {selectedNotification.related_comment_id ? 'Xem bình luận →' : 'Xem bài viết →'}
                    </Link>
                  </div>
                </div>
              )}

              {/* Show related user only if not a message notification (message notifications already show user above) */}
              {selectedNotification.related_user && selectedNotification.type !== 'message' && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-1">Người dùng liên quan:</p>
                  <p className="text-sm text-gray-600">
                    {selectedNotification.related_user.full_name || selectedNotification.related_user.username}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                {!selectedNotification.is_read && (
                  <button
                    onClick={async () => {
                      await handleMarkAsRead(selectedNotification.id);
                      setSelectedNotification({ ...selectedNotification, is_read: true });
                    }}
                    className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition"
                  >
                    Đánh dấu đã đọc
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;

